import React, { useState } from 'react';

const INITIAL_FORM = {
  name:        '',
  email:       '',
  phone:       '',
  inquiryType: '',
  message:     '',
};

const INQUIRY_TYPES = [
  { value: 'general',              label: 'General Inquiry'              },
  { value: 'brand-partnership',    label: 'Brand Partnership'            },
  { value: 'investment-acquisition', label: 'Investment / Acquisition Inquiry' },
  { value: 'media-other',          label: 'Media / Other'                },
];

/**
 * ContactForm — Controlled contact form
 *
 * BACKEND INTEGRATION:
 * Replace the handleSubmit logic with your preferred service:
 *
 * Option A — Formspree (recommended for cPanel hosting):
 *   1. Create account at formspree.io
 *   2. Replace the fetch URL: 'https://formspree.io/f/YOUR_FORM_ID'
 *
 * Option B — PHP Mailer:
 *   POST to '/contact.php' on your server
 *
 * Option C — EmailJS:
 *   Use emailjs.sendForm() from @emailjs/browser package
 */
export default function ContactForm() {
  const [form,       setForm]       = useState(INITIAL_FORM);
  const [status,     setStatus]     = useState(null); // null | 'success' | 'error'
  const [submitting, setSubmitting] = useState(false);
  const [errors,     setErrors]     = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim())      e.name = 'Name is required.';
    if (!form.email.trim())     e.email = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Please enter a valid email.';
    if (!form.inquiryType)      e.inquiryType = 'Please select an inquiry type.';
    if (!form.message.trim())   e.message = 'Please include a message.';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setStatus(null);

    try {
      /*
       * ============================================================
       * REPLACE THIS BLOCK WITH YOUR ACTUAL FORM SUBMISSION LOGIC
       * ============================================================
       * Example (Formspree):
       *
       * const res = await fetch('https://formspree.io/f/YOUR_FORM_ID', {
       *   method: 'POST',
       *   headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
       *   body: JSON.stringify(form),
       * });
       * if (!res.ok) throw new Error('Submission failed');
       * ============================================================
       */
      await new Promise(r => setTimeout(r, 1000)); // Remove in production
      setStatus('success');
      setForm(INITIAL_FORM);
    } catch {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate aria-label="Contact inquiry form">

      {/* Row 1: Name + Email */}
      <div className="form-row-2">
        <div className="form-group">
          <label htmlFor="cf-name" className="form-label">
            Full Name <span aria-hidden="true">*</span>
          </label>
          <input
            id="cf-name"
            name="name"
            type="text"
            className="form-input"
            placeholder="Your full name"
            value={form.name}
            onChange={handleChange}
            autoComplete="name"
            aria-required="true"
            aria-describedby={errors.name ? 'cf-name-err' : undefined}
          />
          {errors.name && (
            <span id="cf-name-err" style={{ color: '#991B1B', fontSize: 'var(--text-xs)', marginTop: '4px' }}>
              {errors.name}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="cf-email" className="form-label">
            Email Address <span aria-hidden="true">*</span>
          </label>
          <input
            id="cf-email"
            name="email"
            type="email"
            className="form-input"
            placeholder="your@email.com"
            value={form.email}
            onChange={handleChange}
            autoComplete="email"
            aria-required="true"
            aria-describedby={errors.email ? 'cf-email-err' : undefined}
          />
          {errors.email && (
            <span id="cf-email-err" style={{ color: '#991B1B', fontSize: 'var(--text-xs)', marginTop: '4px' }}>
              {errors.email}
            </span>
          )}
        </div>
      </div>

      {/* Row 2: Phone + Inquiry Type */}
      <div className="form-row-2">
        <div className="form-group">
          <label htmlFor="cf-phone" className="form-label">Phone Number</label>
          <input
            id="cf-phone"
            name="phone"
            type="tel"
            className="form-input"
            placeholder="Optional"
            value={form.phone}
            onChange={handleChange}
            autoComplete="tel"
          />
        </div>

        <div className="form-group">
          <label htmlFor="cf-inquiry" className="form-label">
            Inquiry Type <span aria-hidden="true">*</span>
          </label>
          <select
            id="cf-inquiry"
            name="inquiryType"
            className="form-select"
            value={form.inquiryType}
            onChange={handleChange}
            aria-required="true"
            aria-describedby={errors.inquiryType ? 'cf-inquiry-err' : undefined}
          >
            <option value="">Select inquiry type…</option>
            {INQUIRY_TYPES.map(({ value, label }) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {errors.inquiryType && (
            <span id="cf-inquiry-err" style={{ color: '#991B1B', fontSize: 'var(--text-xs)', marginTop: '4px' }}>
              {errors.inquiryType}
            </span>
          )}
        </div>
      </div>

      {/* Message */}
      <div className="form-group">
        <label htmlFor="cf-message" className="form-label">
          Message <span aria-hidden="true">*</span>
        </label>
        <textarea
          id="cf-message"
          name="message"
          className="form-textarea"
          placeholder="Please describe your inquiry. We review all submissions and respond within 1–2 business days."
          value={form.message}
          onChange={handleChange}
          aria-required="true"
          aria-describedby={errors.message ? 'cf-msg-err' : undefined}
        />
        {errors.message && (
          <span id="cf-msg-err" style={{ color: '#991B1B', fontSize: 'var(--text-xs)', marginTop: '4px' }}>
            {errors.message}
          </span>
        )}
      </div>

      {/* Submit */}
      <button
        type="submit"
        className="btn btn-primary full-width"
        disabled={submitting}
        style={{ justifyContent: 'center', padding: '1rem', fontSize: 'var(--text-xs)' }}
      >
        {submitting ? 'Sending…' : 'Submit Inquiry'}
      </button>

      <p style={{ marginTop: 'var(--sp-4)', fontSize: 'var(--text-xs)', color: 'var(--gray-500)', textAlign: 'center' }}>
        We respond to all inquiries within 1–2 business days.
      </p>

      {/* Status Messages */}
      {status === 'success' && (
        <div className="form-status form-status--success" role="alert">
          <strong>Your inquiry has been submitted.</strong> Thank you for contacting Driven 2 LLC.
          A member of our team will be in touch within 1–2 business days.
        </div>
      )}

      {status === 'error' && (
        <div className="form-status form-status--error" role="alert">
          There was a problem submitting your message. Please try again or contact us
          directly at{' '}
          <a href="mailto:info@driven2.net" style={{ color: 'inherit', textDecoration: 'underline' }}>
            info@driven2.net
          </a>.
        </div>
      )}

    </form>
  );
}
